import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Avatar,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  ThemeProvider,
  Menu,
  ListItemText,
  ListItemIcon,
  Dialog,
} from "@mui/material";
import {
  Close as CloseIcon,
  Storage as StorageIcon,
  CloudUpload as CloudUploadIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { getAppBarPages, type AdminPageConfig } from "./adminPagesConfig";
import { useSelector, useDispatch } from "react-redux";
import { AppState, AppDispatch } from "../store";
import { useAuth } from "../utils/userAuth";
import { apiClient } from "../utils/apiClient";
import { adminTheme } from "./adminTheme";
import {
  UserInfo,
  OrganizationInfo,
  AdminListUsersResponse,
  AdminUpdateSettingsBody,
  AdminUpdateSettingsResponse,
} from "@services/admin/types";
import {
  fetchAdminInfo,
  selectAdminInfo,
  fetchEnvInfo,
  selectEnvInfo,
} from "../store/systemData";
import { SnackbarProvider } from "../components/SnackbarProvider";
import { ActionLogDetailsModal } from "./components/ActionLogDetailsModal";
import { ErrorDetailsModal } from "./components/ErrorDetailsModal";
import { DatabaseObjectView } from "./DatabaseObjectView";
import { EmailThreadComponent } from "../components/EmailThreadComponent";
import { TimelineSidePanel } from "./shipments/TimelineSidePanel";
import {
  selectDatabaseObjectModal,
  selectEmailModal,
  selectShipmentModal,
  selectActionModal,
  openDatabaseObjectModal,
  closeDatabaseObjectModal,
  closeEmailModal,
  closeShipmentModal,
  closeActionModal,
  fetchOrganizations,
  selectOrganizations,
} from "../store/adminSlice";
import { ResultDetailDrawerWithHandlers } from "./emailAutomationPipeline/components/ResultDetailDrawerWithHandlers";
import { AutomationActionDetailModal } from "./remindersAndActionsAdminDashboard/AutomationActionDetailModal";
import { FileUploadModal } from "./adminCommandsComponents";

interface AdminPageProps {
  children: React.ReactNode;
  title?: string;
  containerStyle?: any;
}

const AdminPage: React.FC<AdminPageProps> = ({ children, containerStyle }) => {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const user = useSelector(
    ({ userData }: AppState) => userData.user,
    (a, b) => a?.id === b?.id
  );

  const adminInfo = useSelector(selectAdminInfo);
  const envInfo = useSelector(selectEnvInfo);

  // Modal states from admin slice
  const databaseObjectModal = useSelector(selectDatabaseObjectModal);
  const emailModal = useSelector(selectEmailModal);
  const shipmentModal = useSelector(selectShipmentModal);
  const actionModal = useSelector(selectActionModal);

  // Organizations from Redux
  const organizationsState = useSelector(selectOrganizations);
  const allOrgs = organizationsState.list;

  const [allUsers, setAllUsers] = useState<UserInfo[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [envMenuAnchor, setEnvMenuAnchor] = useState<null | HTMLElement>(null);
  const [fileUploadModalOpen, setFileUploadModalOpen] = useState(false);

  // Get app bar pages first (used in initial state)
  const appBarPages = getAppBarPages();

  // Responsive app bar state
  const [visibleCount, setVisibleCount] = useState(appBarPages.length);
  const [overflowMenuAnchor, setOverflowMenuAnchor] =
    useState<null | HTMLElement>(null);
  const navContainerRef = useRef<HTMLDivElement>(null);
  const measureContainerRef = useRef<HTMLDivElement>(null);

  // Calculate how many items can fit in the app bar
  const calculateVisibleItems = useCallback(() => {
    if (!navContainerRef.current || !measureContainerRef.current) return;

    const availableWidth = navContainerRef.current.offsetWidth;
    const measureContainer = measureContainerRef.current;
    const items = measureContainer.querySelectorAll("[data-measure-item]");

    // Reserve space for overflow button (about 40px)
    const reservedSpace = 50;
    const gap = 4; // 4px gap between items

    let totalWidth = 0;
    let count = 0;

    // Measure each item from the hidden measure container
    items.forEach((item, index) => {
      const itemWidth = (item as HTMLElement).offsetWidth;
      const widthWithGap = itemWidth + (index > 0 ? gap : 0);

      if (totalWidth + widthWithGap + reservedSpace <= availableWidth) {
        totalWidth += widthWithGap;
        count++;
      }
    });

    // Check if all items fit without needing overflow button
    let totalWidthAllItems = 0;
    items.forEach((item, index) => {
      totalWidthAllItems +=
        (item as HTMLElement).offsetWidth + (index > 0 ? gap : 0);
    });

    if (totalWidthAllItems <= availableWidth) {
      setVisibleCount(appBarPages.length);
    } else {
      setVisibleCount(Math.max(1, count));
    }
  }, [appBarPages.length]);

  // Set up resize observer
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      calculateVisibleItems();
    });

    if (navContainerRef.current) {
      observer.observe(navContainerRef.current);
    }

    // Initial calculation after render
    const timer = setTimeout(calculateVisibleItems, 50);

    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [calculateVisibleItems]);

  const visiblePages = appBarPages.slice(0, visibleCount);
  const overflowPages = appBarPages.slice(visibleCount);

  const handleOverflowMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOverflowMenuAnchor(event.currentTarget);
  };

  const handleOverflowMenuClose = () => {
    setOverflowMenuAnchor(null);
  };

  const handleEnvironmentSwitch = (environment: {
    name: string;
    url: string;
  }) => {
    const currentPath = window.location.pathname + window.location.search;
    const newUrl = environment.url + currentPath;
    window.location.href = newUrl;
  };

  const handleEnvMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setEnvMenuAnchor(event.currentTarget);
  };

  const handleEnvMenuClose = () => {
    setEnvMenuAnchor(null);
  };

  // Fetch users and impersonation status once on mount
  useEffect(() => {
    const fetchData = async () => {
      // Fetch users
      const usersResponse = await apiClient.post<AdminListUsersResponse>(
        "/admin/list_users",
        {}
      );
      setAllUsers(usersResponse.users);

      // Fetch current impersonation status to initialize dropdowns
      try {
        const impersonationStatus = await apiClient.get<{
          isImpersonating: boolean;
          adminEmail?: string;
          impersonatedUser?: {
            id: string;
            email: string;
            organization: string | null;
          } | null;
          impersonatedOrgId?: string | null;
        }>("/admin/impersonation_status");

        if (
          impersonationStatus.isImpersonating &&
          impersonationStatus.impersonatedUser
        ) {
          setSelectedUserId(impersonationStatus.impersonatedUser.id);
        }

        // Set impersonated org if present
        if (impersonationStatus.impersonatedOrgId) {
          setSelectedOrgId(impersonationStatus.impersonatedOrgId);
        }
      } catch (error) {
        console.error("Failed to fetch impersonation status:", error);
      }
    };

    fetchData();
  }, []);

  // Fetch Redux data once on mount (separate effect to avoid re-triggering fetchData)
  useEffect(() => {
    if (!adminInfo) {
      dispatch(fetchAdminInfo());
    }
    if (!envInfo) {
      dispatch(fetchEnvInfo());
    }
    if (allOrgs.length === 0 && !organizationsState.loading) {
      dispatch(fetchOrganizations());
    }
  }, []);

  if (!isLoggedIn && !user?.isAdmin) {
    return <Navigate to={`/user/login?redirectUrl=${encodeURIComponent(window.location.href)}`} replace />;
  }
  return (
    <ThemeProvider theme={adminTheme}>
      <div>
        <AppBar
          position="static"
          sx={{
            backgroundColor: envInfo?.isProd ? "#000000" : undefined,
          }}
        >
          <Toolbar>
            {/* Icon on the top-left */}
            <Avatar
              src="/www/assets/dobby_internal_tools_icon.png"
              alt="Admin Tools Icon"
              onClick={() => navigate("/admin")}
              sx={{
                marginRight: 2,
                width: 40,
                height: 40,
                cursor: "pointer",
              }}
            />

            {/* Environment Badge */}
            {envInfo && (
              <>
                <Box
                  onClick={handleEnvMenuClick}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: "12px",
                    padding: "4px 8px",
                    marginRight: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.15)",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "white",
                      fontWeight: 500,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {envInfo.deploymentEnv}
                  </Typography>
                </Box>
                <Menu
                  anchorEl={envMenuAnchor}
                  open={Boolean(envMenuAnchor)}
                  onClose={handleEnvMenuClose}
                  slotProps={{
                    paper: {
                      sx: {
                        backgroundColor: "#1e1e1e",
                        color: "white",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                      },
                    },
                  }}
                >
                  {envInfo.availableEnvironments?.map((env) => (
                    <MenuItem
                      key={env.name}
                      onClick={() => {
                        handleEnvironmentSwitch(env);
                        handleEnvMenuClose();
                      }}
                      disabled={env.name === envInfo.deploymentEnv}
                      sx={{
                        minWidth: 200,
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                        },
                        "&.Mui-disabled": {
                          color: "rgba(255, 255, 255, 0.5)",
                        },
                      }}
                    >
                      <ListItemText
                        primary={env.label}
                        secondary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                            >
                              {env.url}
                            </Typography>
                            {env.isProd && (
                              <Typography
                                variant="caption"
                                sx={{
                                  backgroundColor: "#d32f2f",
                                  color: "white",
                                  px: 0.5,
                                  py: 0.25,
                                  borderRadius: "4px",
                                  fontSize: "10px",
                                }}
                              >
                                PROD
                              </Typography>
                            )}
                            {env.name === envInfo.deploymentEnv && (
                              <Typography
                                variant="caption"
                                sx={{
                                  backgroundColor: "#2e7d32",
                                  color: "white",
                                  px: 0.5,
                                  py: 0.25,
                                  borderRadius: "4px",
                                  fontSize: "10px",
                                }}
                              >
                                CURRENT
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}

            {/* Hidden container for measuring item widths */}
            <Box
              className="admin-appbar-measure-container"
              ref={measureContainerRef}
              sx={{
                position: "absolute",
                visibility: "hidden",
                display: "flex",
                whiteSpace: "nowrap",
                gap: "8px",
              }}
            >
              {appBarPages.map((page) => (
                <Button
                  key={page.key}
                  data-measure-item
                  color="inherit"
                  startIcon={page.icon}
                  sx={{
                    textTransform: "none",
                    fontSize: "14px",
                    minWidth: "auto",
                    px: 0.5,
                    "& .MuiButton-startIcon": {
                      marginRight: 0,
                      transform: "scale(0.66)",
                    },
                  }}
                >
                  {page.shortTitle || page.title}
                </Button>
              ))}
            </Box>

            {/* Quick Links - Responsive Navigation */}
            <Box
              ref={navContainerRef}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                ml: 1,
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              {visiblePages.map((page) => (
                <Button
                  key={page.key}
                  color="inherit"
                  component={Link}
                  to={page.path}
                  startIcon={page.icon}
                  sx={{
                    textTransform: "none",
                    fontSize: "14px",
                    minWidth: "auto",
                    whiteSpace: "nowrap",
                    px: 0.5,
                    flexShrink: 0,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                    "& .MuiButton-startIcon": {
                      marginRight: 0,
                      transform: "scale(0.6)",
                    },
                  }}
                >
                  {page.shortTitle || page.title}
                </Button>
              ))}
            </Box>

            {/* Overflow Menu Button - outside the nav container */}
            {overflowPages.length > 0 && (
              <>
                <IconButton
                  color="inherit"
                  onClick={handleOverflowMenuOpen}
                  size="small"
                  sx={{
                    flexShrink: 0,
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={overflowMenuAnchor}
                  open={Boolean(overflowMenuAnchor)}
                  onClose={handleOverflowMenuClose}
                  slotProps={{
                    paper: {
                      sx: {
                        backgroundColor: "#1e1e1e",
                        color: "white",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                      },
                    },
                  }}
                >
                  {overflowPages.map((page) => (
                    <MenuItem
                      key={page.key}
                      component={Link}
                      to={page.path}
                      onClick={handleOverflowMenuClose}
                      sx={{
                        fontSize: "14px",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                          textDecoration: "underline",
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: "inherit",
                          minWidth: 24,
                          transform: "scale(0.6)",
                        }}
                      >
                        {page.icon}
                      </ListItemIcon>
                      <ListItemText primary={page.shortTitle || page.title} />
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}

            {/* Database Object Viewer Icon */}
            <IconButton
              color="inherit"
              onClick={() =>
                dispatch(openDatabaseObjectModal({ table: "", id: "" }))
              }
              title="Database Object Viewer"
            >
              <StorageIcon />
            </IconButton>

            {/* File Upload Icon */}
            <IconButton
              color="inherit"
              onClick={() => setFileUploadModalOpen(true)}
              title="Upload File to S3"
            >
              <CloudUploadIcon />
            </IconButton>

            {/* User selection */}
            <FormControl variant="outlined" size="small" sx={{ minWidth: 160 }}>
              <Select
                sx={{ fontSize: 12, color: "white" }}
                value={selectedUserId}
                displayEmpty
                onChange={(e: any) => {
                  const userId = e.target.value;
                  setSelectedUserId(userId);

                  // If user has org, clear org selection since it will be auto-set
                  const selectedUser = allUsers.find((u) => u.id === userId);
                  if (selectedUser?.organizationName || !userId) {
                    setSelectedOrgId("");
                  }

                  // Allow React to re-render, then make API call
                  setTimeout(async () => {
                    const res =
                      await apiClient.post<AdminUpdateSettingsResponse>(
                        "/admin/update_admin_settings",
                        {
                          impersonatedUser: userId || null,
                        } satisfies AdminUpdateSettingsBody
                      );
                    if (res.success) window.location.reload();
                  }, 0);
                }}
              >
                <MenuItem value="">User: {user?.email}</MenuItem>
                {allUsers.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Org selection */}
            <FormControl
              variant="outlined"
              size="small"
              sx={{ minWidth: 160, ml: 2 }}
            >
              <Select
                sx={{ fontSize: 12, color: "white" }}
                value={selectedOrgId}
                displayEmpty
                disabled={(() => {
                  // Disable if impersonating user with org OR if current effective user has org
                  const impersonatedUser = allUsers.find(
                    (u) => u.id === selectedUserId
                  );
                  const effectiveUserHasOrg = selectedUserId
                    ? !!impersonatedUser?.organizationName
                    : !!(user as any)?.orgId;
                  return effectiveUserHasOrg;
                })()}
                onChange={(e: any) => {
                  const orgId = e.target.value;
                  setSelectedOrgId(orgId);

                  // Allow React to re-render, then make API call
                  setTimeout(async () => {
                    const res =
                      await apiClient.post<AdminUpdateSettingsResponse>(
                        "/admin/update_admin_settings",
                        {
                          impersonatedOrg: orgId || null,
                        } satisfies AdminUpdateSettingsBody
                      );
                    if (res.success) window.location.reload();
                  }, 0);
                }}
              >
                <MenuItem value="">
                  Org:{" "}
                  {(() => {
                    // Show current org
                    const impersonatedUser = allUsers.find(
                      (u) => u.id === selectedUserId
                    );
                    const effectiveOrg = selectedUserId
                      ? impersonatedUser?.organizationName
                      : (user as any)?.organization?.name;
                    return effectiveOrg || "None";
                  })()}
                </MenuItem>
                {allOrgs.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Toolbar>
        </AppBar>

        <div style={{ marginTop: "20px", ...(containerStyle || {}) }}>
          {children}
        </div>

        {/* Global Admin Modals */}
        <ActionLogDetailsModal />
        <ErrorDetailsModal />

        {/* Database Object Modal */}
        <DatabaseObjectView
          open={databaseObjectModal.open}
          onClose={() => dispatch(closeDatabaseObjectModal())}
          initialTable={databaseObjectModal.table || ""}
          initialId={databaseObjectModal.id || ""}
          autoFetch={true}
        />

        {/* Email Modal */}
        {emailModal.open && emailModal.emailId && (
          <Dialog
            open={emailModal.open}
            onClose={() => dispatch(closeEmailModal())}
            maxWidth="lg"
            fullWidth
            sx={{
              "& .MuiDialog-paper": {
                height: "90vh",
              },
            }}
          >
            <IconButton
              aria-label="close"
              onClick={() => dispatch(closeEmailModal())}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
                zIndex: 1,
              }}
            >
              <CloseIcon />
            </IconButton>
            <EmailThreadComponent emailId={emailModal.emailId} />
          </Dialog>
        )}

        {/* Shipment Timeline Panel */}
        <TimelineSidePanel
          open={shipmentModal.open}
          onClose={() => dispatch(closeShipmentModal())}
          shipmentId={shipmentModal.shipmentId ?? undefined}
          includeInternalReminders={true}
          isAdmin={true}
        />

        {/* Global Result Detail Drawer */}
        <ResultDetailDrawerWithHandlers />

        {/* Global Action Detail Modal */}
        <AutomationActionDetailModal
          actionId={actionModal.actionId}
          open={actionModal.open}
          onClose={() => dispatch(closeActionModal())}
        />

        {/* File Upload Modal */}
        <FileUploadModal
          open={fileUploadModalOpen}
          onClose={() => setFileUploadModalOpen(false)}
        />
      </div>
    </ThemeProvider>
  );
};

export default AdminPage;
